import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getProjects } from '../api/project.service';

const fetchProjectsWithModules = async () => {
  const { data } = await getProjects();
  return data.data;
};

export default function SidebarItem({ item, isActive, isCollapsed }) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(isActive || location.pathname.startsWith(item.href));

  const isProjects = item.name === 'Projects';

  const { data: projects } = useQuery({
    queryKey: ['projects-sidebar'],
    queryFn: fetchProjectsWithModules,
    enabled: isProjects && isOpen && !isCollapsed,
  });

  // Render collapsed (mini) state
  if (isCollapsed) {
    return (
      <Link
        to={item.href}
        className={`group relative flex items-center justify-center control-inline rounded-xl transition-colors ${
          isActive
            ? 'bg-primary/10 text-primary'
            : 'text-muted-foreground hover:bg-accent hover:text-foreground'
        }`}
      >
        <item.icon className={`density-icon ${isActive ? 'text-primary' : 'opacity-70'}`} />

        {/* Tooltip */}
        <div className="absolute left-full ml-4 px-2 py-1 bg-popover text-popover-foreground text-xs font-medium rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 whitespace-nowrap shadow-md border border-border">
          {item.name}
        </div>
      </Link>
    );
  }

  // Expanded State
  if (isProjects) {
    return (
      <div className="space-y-1">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`nav-item-density w-full flex items-center justify-between rounded-xl text-sm font-medium transition-colors ${
            isActive || isOpen
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:bg-accent hover:text-foreground'
          }`}
        >
          <div className="flex items-center gap-[var(--spacing-section)]">
            <item.icon
              className={`density-icon ${isActive || isOpen ? 'text-primary' : 'text-muted-foreground'}`}
            />
            {item.name}
          </div>
          {isOpen ? (
            <ChevronDown className="h-4 w-4 opacity-50" />
          ) : (
            <ChevronRight className="h-4 w-4 opacity-50" />
          )}
        </button>

        {isOpen && (
          <div className="pl-10 pr-2 py-1 space-y-1 animate-in slide-in-from-top-2 fade-in duration-200">
            <Link
              to="/projects"
              className={`nav-item-density block rounded-xl text-xs font-medium transition-colors ${
                location.pathname === '/projects'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              }`}
            >
              All Projects Overview
            </Link>
            {projects?.map((project) => (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className={`nav-item-density block rounded-xl text-xs font-medium truncate transition-colors ${
                  location.pathname === `/projects/${project.id}`
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
              >
                {project.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.href}
      className={`nav-item-density flex items-center rounded-xl text-sm font-medium transition-colors ${
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground'
      }`}
    >
      <item.icon
        className={`density-icon ${isActive ? 'text-primary-foreground' : 'text-muted-foreground'}`}
      />
      {item.name}
    </Link>
  );
}
